/**
 * MAVLink 帧的常量、类型与错误。
 *
 * 帧的两种线上布局（`STX` 之后的字节数）：
 *
 *   v1:  STX FE | LEN | SEQ | SYS | COMP | MSGID          | PAYLOAD | CRC(2)
 *              └──────────── CRC 覆盖范围 ────────────┘
 *   v2:  STX FD | LEN | INCOMPAT | COMPAT | SEQ | SYS | COMP | MSGID(3) | PAYLOAD | CRC(2) | [SIG(13)]
 *              └───────────────────── CRC 覆盖范围 ─────────────────────┘
 *
 * CRC 不覆盖 STX，也不覆盖它自己；计算完载荷后再把 CRC_EXTRA 累加进去。
 */

export const MAVLINK_STX_V1 = 0xfe;
export const MAVLINK_STX_V2 = 0xfd;

/** `incompat_flags` 里的签名位（MAVLink 2） */
export const MAVLINK_IFLAG_SIGNED = 0x01;

/** STX 之后的头部长度 */
export const MAVLINK_CORE_HEADER_LENGTH_V1 = 5;
export const MAVLINK_CORE_HEADER_LENGTH_V2 = 9;

/** 签名块总长：link_id(1) + timestamp(6) + signature(6) */
export const MAVLINK_SIGNATURE_BLOCK_LENGTH = 13;
/** 签名本身只用 SHA-256 结果的前 6 字节 */
export const MAVLINK_SIGNATURE_HASH_LENGTH = 6;
export const MAVLINK_LINK_ID_LENGTH = 1;
export const MAVLINK_TIMESTAMP_LENGTH = 6;

/** payload 长度字段只有一个字节 */
export const MAVLINK_MAX_PAYLOAD_LENGTH = 255;

/** 系统 / 组件 ID 的取值范围 */
export const MAX_SYSTEM_ID = 255;
export const MAX_COMPONENT_ID = 255;
/** 序号是 8 位，回绕 */
export const MAX_SEQUENCE = 255;

/** MAVLink 2 签名时间戳的纪元：2015-01-01T00:00:00Z */
export const SIGNING_EPOCH_MS = Date.UTC(2015, 0, 1, 0, 0, 0);

/** 签名时间戳的单位：10 微秒 */
export const SIGNING_TICKS_PER_MS = 100;

/** MAVLink 2 时间戳是 48 位 */
export const SIGNING_TIMESTAMP_LIMIT = 2 ** 48;

export interface FrameSignature {
  readonly linkId: number;
  /** 48 位，单位 10µs，自 `SIGNING_EPOCH_MS` 起算 */
  readonly timestamp: number;
  /** 帧里携带的 6 字节签名 */
  readonly hash: Uint8Array;
  /**
   * 是否通过校验。
   *
   * 校验失败**不会**导致丢帧：帧的完整性已经由 CRC 保证，签名证明的是"发信人是谁"。
   * 地面站需要把两者分开呈现 —— "这帧像不像真数据"和"这帧是不是我以为的那台飞机发的"
   * 是两个不同的问题，混在一起会让排障无从下手。
   */
  readonly verified: boolean;
}

export interface MavlinkFrame {
  readonly version: 1 | 2;
  /** 发送序号（8 位，回绕）。缺口用于估算丢包率。 */
  readonly sequence: number;
  readonly systemId: number;
  readonly componentId: number;
  readonly messageId: number;
  /**
   * 载荷，长度等于该消息定义的完整线上长度。
   * MAVLink 2 发送时可能截掉了尾部的零字节，这里**已经零填充补齐**，
   * 因此按字段偏移读取永远安全。
   */
  readonly payload: Uint8Array;
  /** 线上实际出现的载荷长度（MAVLink 2 可能小于完整长度） */
  readonly payloadLength: number;
  readonly incompatFlags: number;
  readonly compatFlags: number;
  /** 本机收到该帧的时刻（毫秒，`performance.now()` 或 `Date.now()` 之外由调用方决定） */
  readonly receivedAt: number;
  readonly signature?: FrameSignature;
}

export class MavlinkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MavlinkError';
  }
}

/** 组帧失败。绝大多数情况是"字段缺失或类型不对"，属于代码缺陷而不是链路问题。 */
export class MavlinkEncodeError extends MavlinkError {
  constructor(message: string) {
    super(message);
    this.name = 'MavlinkEncodeError';
  }
}
